// src/components/RichTextEditor.js
import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import initQuillExtensions from '../utils/QuillExtensions';
import MediaBrowser from '../features/MediaLibrary/MediaBrowser';

const RichTextEditor = ({ 
  value = '',
  onChange = () => {},
  placeholder = 'Enter text here...',
  height = '200px',
  questionId = null
}) => {
  const editorRef = useRef(null);
  const [content, setContent] = useState(value);
  const [showImageSelector, setShowImageSelector] = useState(false);
  const [quillInitialized, setQuillInitialized] = useState(false);
  const quillRef = useRef(null);
  const isMounted = useRef(true);
  
  // Initialize Quill extensions
  useEffect(() => {
    isMounted.current = true;
    
    try {
      if (!window.quillInitialized) {
        initQuillExtensions();
        window.quillInitialized = true;
      }
      
      if (isMounted.current) {
        setQuillInitialized(true);
      }
    } catch (error) {
      console.error('Failed to initialize Quill:', error);
      if (isMounted.current) {
        setQuillInitialized(true);
      }
    }
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Sync with external value changes
  useEffect(() => {
    setContent(value);
  }, [value]);

  // Handle content changes
  const handleContentChange = useCallback((newContent) => {
    setContent(newContent);
    onChange(newContent);
  }, [onChange]);

  // Image handler
  const imageHandler = useCallback(() => {
    setShowImageSelector(true);
  }, []);

  // Handle image selection from library
  const handleSelectImage = useCallback((imageAsset) => {
    if (!quillRef.current || !imageAsset) return;
    
    try {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      
      quill.insertEmbed(range.index, 'image', imageAsset.url);
      quill.setSelection(range.index + 1);
      
      setShowImageSelector(false);
    } catch (error) {
      console.error('Error inserting image:', error);
    }
  }, []);

  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  };
  
  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link', 'image'
  ];

  if (!quillInitialized) {
    return (
      <div style={{border: '1px solid #ccc', borderRadius: '4px'}}>
        <textarea
          value={content}
          onChange={(e) => handleContentChange(e.target.value)}
          placeholder={placeholder}
          style={{
            width: '100%',
            minHeight: height,
            padding: '10px',
            border: 'none',
            borderRadius: '4px'
          }}
        />
      </div>
    );
  }

  return (
    <div className="rich-text-editor" ref={editorRef}>
      <div style={{ height }}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={content}
          onChange={handleContentChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          style={{ height: '100%' }}
        />
      </div>
      
      {showImageSelector && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '20px',
            borderRadius: '8px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
          }}>
            <h2>Select Image from Library</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <div style={{ height: '500px', overflow: 'auto' }}>
                <MediaBrowser
                  mediaType="image"
                  onSelectMedia={handleSelectImage}
                  unitId={questionId}
                  showSearchBar={true}
                  showCategoryFilter={true}
                />
              </div>
            </div>
            
            <div style={{ textAlign: 'right', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #ddd' }}>
              <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px', textAlign: 'left' }}>
                <strong>Note:</strong> To add new images, upload them in the LMS app first. They will then appear here in the library.
              </p>
              <button 
                onClick={() => setShowImageSelector(false)}
                style={{ 
                  padding: '8px 16px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;